import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCmsContent } from "@/hooks/use-cms-content";
import { useTranslation } from "react-i18next";
import defaultLogo from "@/assets/logo-mente-sa.png";
import LanguageSelector from "@/components/LanguageSelector";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [shopEnabled, setShopEnabled] = useState(false);
  const { content } = useCmsContent();
  const { t } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const logo = content.logo_url || defaultLogo;

  useEffect(() => {
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", "shop_enabled")
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          const val = typeof data.value === "string" ? data.value : JSON.stringify(data.value);
          setShopEnabled(val === "true" || val === '"true"');
        }
      });
  }, []);

  // On home page use anchor links, on other pages use route + anchor
  const homeLink = (anchor: string) => (isHome ? anchor : `/${anchor}`);

  const navLinks = [
    { label: t("nav.home"), href: homeLink("#inicio"), isAnchor: true },
    { label: t("nav.services"), href: "/servicos", isAnchor: false },
    { label: t("nav.about"), href: homeLink("#sobre"), isAnchor: true },
    { label: t("nav.testimonials"), href: homeLink("#testemunhos"), isAnchor: true },
    { label: t("nav.contact"), href: homeLink("#contacto"), isAnchor: true },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto flex items-center justify-between py-3 px-6">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="Mente Sã" className="h-12 w-12 rounded-full object-cover" width={48} height={48} />
          <span className="font-display text-2xl font-bold text-primary">Mente Sã</span>
        </Link>

        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              {link.isAnchor && isHome ? (
                <a
                  href={link.href}
                  className="font-body text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.href}
                  className={`font-body text-sm font-medium transition-colors ${
                    location.pathname === link.href ? "text-primary" : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
          {shopEnabled && (
            <li>
              <Link
                to="/loja"
                className="font-body text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5"
              >
                <ShoppingBag size={15} />
                {t("nav.shop")}
              </Link>
            </li>
          )}
          <li>
            <LanguageSelector />
          </li>
          <li>
            {isHome ? (
              <a
                href="#agendar"
                className="inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
              >
                {t("nav.book")}
              </a>
            ) : (
              <Link
                to="/#agendar"
                className="inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
              >
                {t("nav.book")}
              </Link>
            )}
          </li>
        </ul>

        <button className="md:hidden text-foreground" onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-b border-border"
          >
            <ul className="flex flex-col items-center gap-4 py-6">
              {navLinks.map((link) => (
                <li key={link.href}>
                  {link.isAnchor && isHome ? (
                    <a
                      href={link.href}
                      onClick={() => setOpen(false)}
                      className="font-body text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className="font-body text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
              {shopEnabled && (
                <li>
                  <Link
                    to="/loja"
                    onClick={() => setOpen(false)}
                    className="font-body text-base text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5"
                  >
                    <ShoppingBag size={16} />
                    {t("nav.shop")}
                  </Link>
                </li>
              )}
              <li>
                <LanguageSelector />
              </li>
              <li>
                <Link
                  to="/#agendar"
                  onClick={() => setOpen(false)}
                  className="inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground"
                >
                  {t("nav.book")}
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
